import { useState } from 'react'
import { usePageTitle } from '@components/layout/MainLayout'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  LinearProgress,
  Avatar,
  Alert,
} from '@mui/material'
import {
  People,
  Explore,
  Flag,
  Analytics,
  TrendingUp,
  MoreVert,
  Search,
  Edit,
  Delete,
  Block,
  Settings,
} from '@mui/icons-material'
import { DayRange } from '@api/types/dashboard'
import { useDashboardStatistics } from '../hooks/useDashboardStatistics'
import {
  DaysRangeSelector,
  StatsSummaryCards,
  UserActivityChart,
  CompletionsChart,
  CategoryBreakdownChart,
} from '../components/dashboard'


interface User {
  id: string
  username: string
  email: string
  status: 'active' | 'suspended' | 'banned'
  role: string
  joinDate: string
  lastActive: string
  adventuresCreated: number
  reportsCount: number
}

interface Adventure {
  id: string
  title: string
  creator: string
  status: 'active' | 'draft' | 'flagged' | 'archived'
  participants: number
  rating: number
  created: string
  flags: number
}

interface Report {
  id: string
  type: 'user' | 'adventure' | 'content'
  targetId: string
  targetName: string
  reporter: string
  reason: string
  status: 'pending' | 'reviewing' | 'resolved' | 'dismissed'
  created: string
  priority: 'low' | 'medium' | 'high'
}

// Mock data for tabs (still used)

const mockUsers: User[] = [
  {
    id: '1',
    username: 'trail_master_99',
    email: 'sarah@example.com',
    status: 'active',
    role: 'User',
    joinDate: '2024-03-15',
    lastActive: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
    adventuresCreated: 127,
    reportsCount: 0,
  },
  {
    id: '2',
    username: 'spammer_bot',
    email: 'spam@example.com',
    status: 'suspended',
    role: 'User',
    joinDate: '2025-09-28',
    lastActive: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
    adventuresCreated: 0,
    reportsCount: 5,
  },
  {
    id: '3',
    username: 'urban_explorer',
    email: 'mike@example.com',
    status: 'active',
    role: 'Moderator',
    joinDate: '2024-05-20',
    lastActive: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
    adventuresCreated: 115,
    reportsCount: 0,
  },
]

const mockAdventures: Adventure[] = [
  {
    id: '1',
    title: 'Historic Downtown Discovery',
    creator: 'trail_master_99',
    status: 'active',
    participants: 342,
    rating: 4.7,
    created: '2024-08-15',
    flags: 0,
  },
  {
    id: '2',
    title: 'Inappropriate Content Adventure',
    creator: 'spammer_bot',
    status: 'flagged',
    participants: 2,
    rating: 1.2,
    created: '2025-09-28',
    flags: 8,
  },
  {
    id: '3',
    title: 'Nature Trail Explorer',
    creator: 'urban_explorer',
    status: 'active',
    participants: 187,
    rating: 4.9,
    created: '2024-07-20',
    flags: 0,
  },
]

const mockReports: Report[] = [
  {
    id: '1',
    type: 'adventure',
    targetId: '2',
    targetName: 'Inappropriate Content Adventure',
    reporter: 'trail_master_99',
    reason: 'Contains inappropriate content and spam',
    status: 'pending',
    created: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
    priority: 'high',
  },
  {
    id: '2',
    type: 'user',
    targetId: '2',
    targetName: 'spammer_bot',
    reporter: 'urban_explorer',
    reason: 'Spamming and inappropriate behavior',
    status: 'reviewing',
    created: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(),
    priority: 'high',
  },
  {
    id: '3',
    type: 'content',
    targetId: '45',
    targetName: 'Comment on "Street Art Hunt"',
    reporter: 'nature_wanderer',
    reason: 'Offensive language',
    status: 'pending',
    created: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
    priority: 'medium',
  },
]

export default function AdminDashboard() {
  usePageTitle('Dashboard')
  const [activeTab, setActiveTab] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedItem, setSelectedItem] = useState<string | null>(null)
  const [daysRange, setDaysRange] = useState<DayRange>(30)

  const { data: dashboardStats, isLoading, error } = useDashboardStatistics(daysRange)

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, itemId: string) => {
    setAnchorEl(event.currentTarget)
    setSelectedItem(itemId)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedItem(null)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success'
      case 'suspended':
      case 'flagged':
        return 'warning'
      case 'banned':
        return 'error'
      case 'pending':
      case 'reviewing':
        return 'info'
      default:
        return 'default'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error'
      case 'medium':
        return 'warning'
      case 'low':
        return 'info'
      default:
        return 'default'
    }
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
        <DaysRangeSelector value={daysRange} onChange={setDaysRange} />
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load dashboard statistics. Please try again later.
        </Alert>
      )}

      {/* Stats Summary Cards */}
      <Box sx={{ mb: 4 }}>
        <StatsSummaryCards
          totalUsers={dashboardStats?.totalUsers}
          totalAdventures={dashboardStats?.totalAdventures}
          totalGroups={dashboardStats?.totalGroups}
          isLoading={isLoading}
        />
      </Box>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <UserActivityChart data={dashboardStats?.dailyStats} isLoading={isLoading} />
        </Grid>
        <Grid item xs={12} md={6}>
          <CompletionsChart data={dashboardStats?.dailyStats} isLoading={isLoading} />
        </Grid>
        <Grid item xs={12}>
          <CategoryBreakdownChart data={dashboardStats?.dailyStats} isLoading={isLoading} />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab icon={<People />} label="Users" iconPosition="start" />
          <Tab icon={<Explore />} label="Adventures" iconPosition="start" />
          <Tab icon={<Flag />} label="Reports" iconPosition="start" />
          <Tab icon={<Settings />} label="System" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Users Tab */}
      {activeTab === 0 && (
        <Paper>
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <TextField
              fullWidth
              placeholder="Search users by username or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>User</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Role</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Adventures</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Last Active</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Reports</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mockUsers.map((user) => (
                  <TableRow key={user.id} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar>{user.username[0].toUpperCase()}</Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {user.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.status}
                        color={getStatusColor(user.status) as any}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>{user.role}</TableCell>
                    <TableCell>{user.adventuresCreated}</TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {new Date(user.lastActive).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {user.reportsCount > 0 ? (
                        <Chip label={user.reportsCount} color="error" size="small" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          0
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, user.id)}>
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Adventures Tab */}
      {activeTab === 1 && (
        <Paper>
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <TextField
              fullWidth
              placeholder="Search adventures..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'secondary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Title</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Creator</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Participants</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Rating</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Flags</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mockAdventures.map((adventure) => (
                  <TableRow
                    key={adventure.id}
                    sx={{
                      '&:hover': { backgroundColor: 'action.hover' },
                      backgroundColor: adventure.status === 'flagged' ? 'error.50' : 'inherit',
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {adventure.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Created {new Date(adventure.created).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>{adventure.creator}</TableCell>
                    <TableCell>
                      <Chip
                        label={adventure.status}
                        color={getStatusColor(adventure.status) as any}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>{adventure.participants}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {adventure.rating}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          / 5.0
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {adventure.flags > 0 ? (
                        <Chip label={adventure.flags} color="error" size="small" icon={<Flag />} />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          0
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, adventure.id)}>
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Reports Tab */}
      {activeTab === 2 && (
        <Paper>
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                fullWidth
                placeholder="Search reports..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              <Button variant="outlined" size="small">
                Filter
              </Button>
            </Box>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'error.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Target</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Reporter</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Reason</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Priority</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Created</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mockReports.map((report) => (
                  <TableRow key={report.id} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell>
                      <Chip
                        label={report.type}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {report.targetName}
                      </Typography>
                    </TableCell>
                    <TableCell>{report.reporter}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {report.reason}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={report.priority}
                        color={getPriorityColor(report.priority) as any}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={report.status}
                        color={getStatusColor(report.status) as any}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {new Date(report.created).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, report.id)}>
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* System Tab */}
      {activeTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                System Health
              </Typography>
              <Box sx={{ my: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">API Response Time</Typography>
                  <Chip label="Healthy" color="success" size="small" />
                </Box>
                <LinearProgress variant="determinate" value={15} sx={{ height: 8, borderRadius: 4 }} />
                <Typography variant="caption" color="text.secondary">
                  Average: 150ms
                </Typography>
              </Box>
              <Box sx={{ my: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Database Performance</Typography>
                  <Chip label="Good" color="success" size="small" />
                </Box>
                <LinearProgress variant="determinate" value={25} color="success" sx={{ height: 8, borderRadius: 4 }} />
                <Typography variant="caption" color="text.secondary">
                  Query time: 25ms avg
                </Typography>
              </Box>
              <Box sx={{ my: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Server Load</Typography>
                  <Chip label="Normal" color="info" size="small" />
                </Box>
                <LinearProgress variant="determinate" value={45} color="info" sx={{ height: 8, borderRadius: 4 }} />
                <Typography variant="caption" color="text.secondary">
                  45% CPU utilization
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button variant="outlined" fullWidth startIcon={<People />}>
                  Export User Data
                </Button>
                <Button variant="outlined" fullWidth startIcon={<Explore />}>
                  Generate Adventure Report
                </Button>
                <Button variant="outlined" fullWidth startIcon={<Analytics />}>
                  View Analytics Dashboard
                </Button>
                <Button variant="outlined" fullWidth startIcon={<Settings />}>
                  System Configuration
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Action Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleMenuClose}>
          <Edit sx={{ mr: 1 }} fontSize="small" />
          Edit
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <Block sx={{ mr: 1 }} fontSize="small" />
          Suspend
        </MenuItem>
        <MenuItem onClick={handleMenuClose} sx={{ color: 'error.main' }}>
          <Delete sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  )
}
